import {
  BoreholeConfirmationType,
  BoreholeFieldConfirmationData,
  BuildingFragment,
  EnergyBusFragment,
  LocalGridFragment,
} from '../generated/graphql'

const CONFIRMATION_VALIDITY_THRESHOLD = 1

enum MonthlyDataKey {
  HeatingMax = 'holeUsageForHeatingMonthlyMax',
  HeatingTotal = 'holeUsageForHeatingMonthlyTotal',
  CoolingMax = 'holeUsageForCoolingMonthlyMax',
  CoolingTotal = 'holeUsageForCoolingMonthlyTotal',
}

export type BoreholeFieldContainerFragment =
  | BuildingFragment
  | LocalGridFragment
  | EnergyBusFragment

export type BoreholeFieldContainerWithMonthlyUsages =
  BoreholeFieldContainerFragment

const isWithinThreshold = (value: number, comparisonValue: number) =>
  Math.abs(value - comparisonValue) < CONFIRMATION_VALIDITY_THRESHOLD

export function isConfirmationValid(
  container: BoreholeFieldContainerFragment,
  currentData: BoreholeFieldConfirmationData,
): boolean {
  if (container.confirmationType === BoreholeConfirmationType.NoConfirmation) {
    return true
  }

  const { confirmation } = container

  if (!confirmation) {
    return false
  }

  if (container.confirmationType !== confirmation.type) {
    return false
  }

  if (!isWithinThreshold(container.holeCount, confirmation.data.holeCount)) {
    return false
  }

  if (!isWithinThreshold(container.holeDepth, confirmation.data.holeDepth)) {
    return false
  }

  const isMonthlyDataValid = (dataKey: MonthlyDataKey) => {
    const containerValues = currentData[dataKey] ?? []
    const confirmationValues = confirmation.data[dataKey] ?? []

    if (containerValues.length !== confirmationValues.length) {
      return false
    }

    if (containerValues.length === 0) {
      return true
    }

    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const containerValue = containerValues[monthIndex]
      const confirmationValue = confirmationValues[monthIndex]
      if (!isWithinThreshold(containerValue, confirmationValue)) {
        return false
      }
    }

    return true
  }

  const invalid = Object.values(MonthlyDataKey).some(
    (dataKey) => !isMonthlyDataValid(dataKey),
  )

  return !invalid
}
